import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import { useCanaryVisibilityScoreByCompany } from '@/api/aiVisibility';
import WidgetError from '@/components/errors/WidgetError';
import Flare, { Gauge } from '@/components/Flare';
import { Panel } from '@/components/panels';
import { Skeleton } from '@/components/Skeleton';
import { Text } from '@/components/typography';
import { numberFormat } from '@/helper/numberFormatter';
import { cn } from '@/lib/utils';

import AIVisibilityInsightsSummary from './AIVisibilityInsightsSummary';
import AIVisibilityOverviewCompetitorTable from './AIVisibilityOverviewCompetitorTable';
import AIVisibilityOverviewProductAreaChart from './AIVisibilityOverviewProductAreaChart';
import AIVisibilityOverviewTopGaps from './AIVisibilityOverviewTopGaps';
import AIVisibilityOverviewTrendChart from './AIVisibilityOverviewTrendChart';
import AIVisibilityProviderChart from './AIVisibilityProviderChart';

const AIVisibilityOverview = () => {
  const { scores, isLoading, error } = useCanaryVisibilityScoreByCompany();
  const visibilityScore = scores?.customer?.overallScore;
  const components = [
    {
      label: t`Brand`,
      score: scores?.customer?.aiDiscoveryScore,
      color: 'bg-aqua-100',
    },
    {
      label: t`Product Areas`,
      score: scores?.customer?.competitiveComparisonsScore,
      color: 'bg-aqua-400',
    },
    {
      label: t`Sentiment`,
      score: scores?.customer?.sentimentScore,
      color: 'bg-aqua-600',
    },
  ];

  return (
    <div className="flex flex-col gap-5 pt-6">
      <AIVisibilityInsightsSummary />

      <div className="grid gap-4 xl:grid-cols-3">
        <Panel
          title={<Trans>Visibility index</Trans>}
          subtitle={
            <Trans>
              Your brand’s performance across AI discovery, sentiment, and competitive comparisons.
            </Trans>
          }
          className="@container min-h-64 min-w-0"
        >
          {error ? (
            <WidgetError message={<Trans>Unable to load visibility index.</Trans>} />
          ) : (
            <div className="mx-auto flex w-full max-w-lg flex-1 items-center justify-center gap-2 py-3 @sm:gap-6">
              <div
                className="relative flex size-32 shrink-0 flex-col items-center justify-center @xs:size-40 @sm:size-50"
                role={visibilityScore == null ? undefined : 'meter'}
                aria-label={t`Visibility index`}
                aria-valuemin={visibilityScore == null ? undefined : 0}
                aria-valuemax={visibilityScore == null ? undefined : 100}
                aria-valuenow={visibilityScore ?? undefined}
                aria-busy={isLoading}
              >
                <div className="absolute inset-0" aria-hidden="true">
                  <Flare
                    data={[
                      {
                        id: 'visibility-index',
                        name: t`Visibility index`,
                        data: [
                          {
                            score:
                              visibilityScore == null || visibilityScore === 0
                                ? null
                                : visibilityScore / 100,
                          },
                        ],
                      },
                    ]}
                    colors={['var(--color-primary)']}
                    spacingTop={0}
                    spacingBottom={0}
                    spacingLeft={0}
                    spacingRight={0}
                    noPadding
                    error={error}
                    options={{
                      chart: { margin: 0, animation: false },
                      plotOptions: { series: { animation: false, enableMouseTracking: false } },
                      tooltip: { enabled: false },
                      accessibility: { enabled: false },
                    }}
                  >
                    <Gauge
                      y="score"
                      innerSize={0.9}
                      backgroundColor="var(--color-gray-200)"
                      rounded
                    />
                  </Flare>
                </div>
                {isLoading ? (
                  <Skeleton className="h-10 w-20" />
                ) : (
                  <span className="relative text-3xl font-semibold text-gray-900 @sm:text-4xl">
                    {visibilityScore == null
                      ? '—'
                      : numberFormat(visibilityScore, { precision: 0 })}
                  </span>
                )}
                <span className="relative text-sm text-gray-500">
                  <Trans>/ 100</Trans>
                </span>
              </div>
              <div
                className="flex max-w-72 min-w-0 flex-1 flex-col gap-4"
                role="group"
                aria-label={t`Visibility score components`}
                aria-busy={isLoading}
              >
                {components.map(({ label, score, color }) => (
                  <div key={label} className="flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <Text variant="body2">{label}</Text>
                      {isLoading ? (
                        <Skeleton className="h-5 w-6 shrink-0" />
                      ) : (
                        <Text variant="body2" className="shrink-0 text-gray-700">
                          {score == null ? '—' : numberFormat(score, { precision: 0 })}
                        </Text>
                      )}
                    </div>
                    {isLoading ? (
                      <Skeleton className="h-2 w-full rounded-full" />
                    ) : (
                      <div
                        className="h-2 rounded-full bg-gray-200"
                        role={score == null ? undefined : 'meter'}
                        aria-label={label}
                        aria-valuemin={score == null ? undefined : 0}
                        aria-valuemax={score == null ? undefined : 100}
                        aria-valuenow={score ?? undefined}
                      >
                        {score != null && (
                          <div
                            className={cn('h-full rounded-full', color)}
                            style={{ width: `${Math.max(0, Math.min(score, 100))}%` }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Panel>
        <AIVisibilityProviderChart />
        <AIVisibilityOverviewTrendChart />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <AIVisibilityOverviewCompetitorTable />
        <AIVisibilityOverviewProductAreaChart />
      </div>

      <AIVisibilityOverviewTopGaps />
    </div>
  );
};

export default AIVisibilityOverview;
